/**
 * useSkills Hook
 *
 * Thin wrapper around the Zustand skill store.
 * Used by the /skill picker dropdown in the task page.
 */

import { useEffect } from "react";
import { useSession } from "./useSession";
import { useSkillStore } from "@/stores/skillStore";
import type { SkillSummary } from "@bash/shared";

interface UseSkillsResult {
  skills: SkillSummary[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useSkills(): UseSkillsResult {
  const { session } = useSession();
  const skills = useSkillStore((s) => s.mySkills);
  const loading = useSkillStore((s) => s.loading);
  const error = useSkillStore((s) => s.error);
  const hasFetched = useSkillStore((s) => s._hasFetched);
  const setAuthToken = useSkillStore((s) => s.setAuthToken);
  const fetchMySkills = useSkillStore((s) => s.fetchMySkills);

  useEffect(() => {
    if (session?.access_token) {
      setAuthToken(session.access_token);
      if (!hasFetched) {
        fetchMySkills();
      }
    }
  }, [session?.access_token, hasFetched, setAuthToken, fetchMySkills]);

  return {
    skills,
    loading,
    error,
    refetch: fetchMySkills,
  };
}
